import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { useForm, useWatch } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import {
  MAX_LENGTH,
  MIN_LENGTH,
  PasswordField,
  Requirements,
  UsernameForManager,
  passwordRules,
  passwordValid,
} from "@/components/password-fields";
import { Button } from "@/components/ui/button";
import { errorMessage } from "@/services/api";
import { profileQuery, useChangePassword } from "@/services/queries";
import { useAuthStore } from "@/store/auth";
import { useTranslation } from "@/store/locale";

const schema = z
  .object({
    current_password: z.string().min(1),
    new_password: z.string().min(MIN_LENGTH).max(MAX_LENGTH).refine(passwordValid),
    confirm_password: z.string().min(1),
  })
  .refine((values) => values.new_password === values.confirm_password, {
    path: ["confirm_password"],
  })
  .refine((values) => values.new_password !== values.current_password, {
    path: ["new_password"],
  });

type ChangeInput = z.infer<typeof schema>;

/**
 * Current password, new one, confirm it — the same three fields wherever a
 * signed-in person changes their password.
 *
 * Two screens render this: the one inside the app that someone chooses to
 * open, and the full-screen wall a forced change or an expiry puts in the way.
 * They differ in their chrome and in what they say about *why* you are there,
 * not in the form, so only the form lives here.
 */
export function ChangePasswordForm({
  notice,
  footer,
  onDone,
  size = "xl",
}: {
  /** Why this screen opened, when something other than the person opened it. */
  notice?: ReactNode;
  /**
   * Rendered between the checklist and the button — a way out, where there is
   * one. Given whether the change is in flight, because leaving mid-request
   * abandons a call that may already have replaced the password.
   */
  footer?: (saving: boolean) => ReactNode;
  /** Where to go once the password is changed. */
  onDone: () => void;
  size?: "lg" | "xl";
}) {
  const t = useTranslation();
  const queryClient = useQueryClient();
  const setTokens = useAuthStore((state) => state.setTokens);
  const mutation = useChangePassword();
  const { data: profile } = useQuery(profileQuery());

  const form = useForm<ChangeInput>({
    resolver: zodResolver(schema),
    mode: "onChange",
    defaultValues: { current_password: "", new_password: "", confirm_password: "" },
  });
  const [current, next, confirm] = useWatch({
    control: form.control,
    name: ["current_password", "new_password", "confirm_password"],
  });
  const errors = form.formState.errors;

  const rules = [
    ...passwordRules(next),
    { key: "pw.ruleDifferent" as const, met: next.length > 0 && next !== current },
    { key: "pw.ruleMatch" as const, met: next.length > 0 && next === confirm },
  ];
  const ready = rules.every((rule) => rule.met) && current.length > 0;

  const submit = form.handleSubmit((values) =>
    mutation.mutate(
      { current_password: values.current_password, new_password: values.new_password },
      {
        onSuccess: async (tokens) => {
          // Changing a credential revokes every session for this account. The
          // API hands back a fresh pair for the one that made the change —
          // drop it and this tab is holding revoked tokens, so the next
          // request 401s and the person is signed out for succeeding.
          setTokens(tokens.access_token, tokens.refresh_token);
          // The shell's guard reads the cached profile, which still says a change
          // is required: refetch it first, or home bounces straight back here.
          await queryClient.fetchQuery({ ...profileQuery(), staleTime: 0 });
          toast.success(t("pw.success"));
          onDone();
        },
        onError: (error) => toast.error(errorMessage(error, t("pw.failed"))),
      },
    ),
  );

  return (
    <form className="grid gap-4" onSubmit={submit} noValidate>
      {notice}

      <UsernameForManager value={profile?.user_name ?? ""} />

      <PasswordField
        id="current_password"
        label={t("pw.current")}
        autoComplete="current-password"
        error={errors.current_password && t("pw.errorCurrent")}
        register={form.register("current_password")}
      />
      <PasswordField
        id="new_password"
        label={t("pw.new")}
        autoComplete="new-password"
        register={form.register("new_password")}
      />
      <PasswordField
        id="confirm_password"
        label={t("pw.confirm")}
        autoComplete="new-password"
        register={form.register("confirm_password")}
      />

      <Requirements rules={rules} />

      {footer?.(mutation.isPending)}

      <Button
        type="submit"
        size={size}
        className="mt-2 w-full"
        disabled={!ready}
        loading={mutation.isPending}
        loadingLabel={t("pw.saving")}
      >
        {t("pw.submit")}
      </Button>
    </form>
  );
}
